import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@/types'
import { llmPreferenceService } from '@/services/llmPreference'
import { ttsService } from '@/services/tts'
import { drainStableSentences, splitStaticText } from '@/chat/ttsSentenceSplitter'

interface UserState {
  user: User | null
  isAuthenticated: boolean
  setUser: (user: User | null) => void
  logout: () => void
  refreshMetadata: () => void // 新增：刷新元数据
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      setUser: (user) => {
        // 从 localStorage 读取 AIE 元数据并合并到用户状态
        const metadataStr = localStorage.getItem('aie-user-metadata')
        let finalUser: User | null = user

        if (metadataStr && user) {
          try {
            const savedMetadata = JSON.parse(metadataStr)
            console.log('[useUserStore] setUser - 从 localStorage 读取到元数据:', savedMetadata)
            finalUser = {
              ...user,
              metadata: {
                ...user.metadata,
                ...savedMetadata,
              },
            }
            console.log('[useUserStore] setUser - 合并后的用户元数据:', finalUser.metadata)
          } catch (error) {
            console.error('[useUserStore] 解析元数据失败:', error)
            finalUser = user
          }
        }

        set({ user: finalUser, isAuthenticated: !!finalUser })
      },
      logout: () => {
        // 注意：不清除 aie-user-metadata，只清除用户会话
        set({ user: null, isAuthenticated: false })
      },
      refreshMetadata: () => {
        // 从 localStorage 读取 AIE 元数据并合并到用户状态
        const metadataStr = localStorage.getItem('aie-user-metadata')
        if (metadataStr) {
          try {
            const metadata = JSON.parse(metadataStr)
            const currentUser = get().user
            if (currentUser) {
              set({
                user: {
                  ...currentUser,
                  metadata: {
                    ...currentUser.metadata,
                    ...metadata,
                  },
                },
              })
              console.log('[useUserStore] 已刷新元数据:', metadata)
            }
          } catch (error) {
            console.error('[useUserStore] 解析元数据失败:', error)
          }
        }
      },
    }),
    {
      name: 'aie-user-storage',
    }
  )
)

interface UIState {
  theme: 'light' | 'dark'
  sidebarOpen: boolean
  /** 思考过程展示模式：adaptive=长思考自动收成进度卡，短思考照常；full=始终完整逐字（逃生舱） */
  thinkingDisplay: 'adaptive' | 'full'
  setTheme: (theme: 'light' | 'dark') => void
  setSidebarOpen: (open: boolean) => void
  setThinkingDisplay: (mode: 'adaptive' | 'full') => void
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      theme: 'light',
      sidebarOpen: false,
      thinkingDisplay: 'adaptive',
      setTheme: (theme) => set({ theme }),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setThinkingDisplay: (mode) => set({ thinkingDisplay: mode }),
    }),
    {
      name: 'aie-ui-storage',
    }
  )
)

interface ChatState {
  currentConversationId: string | null
  setCurrentConversationId: (id: string | null) => void
}

export const useChatStore = create<ChatState>((set) => ({
  currentConversationId: null,
  setCurrentConversationId: (id) => set({ currentConversationId: id }),
}))

interface LLMPreferenceState {
  provider: string
  model: string
  isLoaded: boolean
  setPreference: (provider: string, model: string) => void
  loadPreference: () => Promise<void>
}

export const useLLMPreferenceStore = create<LLMPreferenceState>()(
  persist(
    (set, get) => ({
      provider: 'Fai-Q 小伙伴',
      model: 'qwen-flash-character',
      isLoaded: false,
      setPreference: (provider, model) => set({ provider, model }),
      loadPreference: async () => {
        if (get().isLoaded) return
        try {
          const pref = await llmPreferenceService.getPreference()
          if (pref) {
            set({ provider: pref.provider, model: pref.model, isLoaded: true })
          } else {
            set({ isLoaded: true })
          }
        } catch {
          set({ isLoaded: true })
        }
      },
    }),
    { name: 'aie-llm-preference' },
  ),
)

// ─── 语音偏好（默认音色 + 自动预合成开关，后端持久化 + 本地镜像）─────────────
interface VoicePreferenceState {
  voice: string
  autoPrefetch: boolean
  isLoaded: boolean
  setVoice: (v: string) => void
  setAutoPrefetch: (v: boolean) => void
  loadPreference: () => Promise<void>
}

export const useVoicePreferenceStore = create<VoicePreferenceState>()(
  persist(
    (set, get) => ({
      voice: 'Cherry',
      autoPrefetch: false,
      isLoaded: false,
      setVoice: (voice) => set({ voice }),
      setAutoPrefetch: (autoPrefetch) => set({ autoPrefetch }),
      loadPreference: async () => {
        if (get().isLoaded) return
        try {
          const pref = await ttsService.getPreference()
          set({ voice: pref.voice, autoPrefetch: pref.autoPrefetch, isLoaded: true })
        } catch {
          set({ isLoaded: true })
        }
      },
    }),
    { name: 'aie-voice-preference' },
  ),
)

// ─── 语音播放器（全局单一 Audio 实例，一次只播一条；多段 URL 顺序播放）─────
type TTSStatus = 'idle' | 'loading' | 'playing' | 'paused'
type TTSError = 'quota' | 'rate_limited' | 'failed' | null
interface TTSPlayerState {
  status: TTSStatus
  currentMessageId: string | null
  error: TTSError
  play: (messageId: string, text: string) => void
  /** 整段预合成播放（睡前故事页 2026-10-11：一次性传入全部音频 url，
   *  规避逐句管线「合成落后于播放 → ended 清会话 → 后续句孤儿化」的竞态） */
  playPrepared: (messageId: string, urls: string[]) => void
  /** 追加预合成音频（故事页分块流水线）：正在播/暂停则入队；会话已被 ended 收掉
   *  则自动从头续播这批 url（迟到不丢，兜底无竞态） */
  appendPrepared: (messageId: string, urls: string[]) => void
  stop: () => void
  /** 流式增量预合成：text 为当前完整 textContent，内部按 consumedLen 只处理新增部分 */
  prefetchStream: (messageId: string, text: string) => void
  /** 流式结束冲刷：把切句缓冲残留整体送合成（autoPrefetch 用户） */
  prefetchFlush: (messageId: string) => void
}

// ─── 类型定义 ───
type SegmentStatus = 'ready' | 'pending' | 'failed'
export interface Segment {
  text: string
  urls: string[]
  status: SegmentStatus
  retried?: boolean // 防重复重试标记（旧字段，兼容保留）
  attempts?: number // 合成尝试次数（2026-10-11：失败段退避重试至 3 次，耗尽才跳段并计入 skippedCount）
}
interface SynthCache {
  segments: Segment[]
  pendingBuffer: string // 切句剩余（短句累积，未达 minLen）
  consumedLen: number // 已从 textContent 消费的长度（防重）
  quotaExhausted: boolean // 泡泡不足 → 停止后续增量
  lastError: 'rate_limited' | 'failed' | null // 最近一次段级失败归因（消费循环终态时给 UI）
  skippedCount: number // 重试耗尽仍失败的段数（2026-10-11：不再无声跳过，播完向 UI 报告）
}
const ttsSynthCache = new Map<string, SynthCache>()

function synthKey(messageId: string, voice: string) {
  return `${messageId}:${voice}`
}
function getSynth(messageId: string, voice: string): SynthCache {
  const key = synthKey(messageId, voice)
  let entry = ttsSynthCache.get(key)
  if (!entry) {
    entry = { segments: [], pendingBuffer: '', consumedLen: 0, quotaExhausted: false, lastError: null, skippedCount: 0 }
    ttsSynthCache.set(key, entry)
  }
  return entry
}

// ─── 合成串行队列（DashScope qwen3-tts-flash 有平台 QPS 限制，逐句并发会撞 429）────────────
// 所有合成调用（prefetchStream 多实例 / play 兜底 / failed 重试）排成一条全局队，一次只合成一段。
// 边播边合：首段≈单段合成(~1s)，后续段在前段播放期间合成，用户无感。
let synthChain: Promise<unknown> = Promise.resolve()
function runSynth<T>(fn: () => Promise<T>): Promise<T> {
  const run = synthChain.then(fn, () => fn()) // 前段完成（无论成败）才跑本段，串行不并发
  synthChain = run.then(
    () => undefined,
    () => undefined,
  ) // 链不断：无论本段成败都推进，不因一段失败卡死后续
  return run as Promise<T>
}

// ─── Audio 播放单例（非响应式）────────────
let ttsAudioEl: HTMLAudioElement | null = null
let ttsQueue: string[] = []
let ttsQueueIdx = 0

// ─── Quota 保护（防止并发多段同时提交，第一段 quota 后后续段立即停止）────────────
const pendingSegments = new Set<string>() // 正在提交的段 key = `${messageId}:${voice}:${text}`

export const useTTSPlayerStore = create<TTSPlayerState>((set, get) => {
  const ensureAudio = () => {
    if (ttsAudioEl) {
      return ttsAudioEl
    }
    ttsAudioEl = new Audio()
    ttsAudioEl.addEventListener('ended', () => {
      const next = ttsQueueIdx + 1
      if (next < ttsQueue.length) {
        ttsQueueIdx = next
        ttsAudioEl!.src = ttsQueue[next]
        void ttsAudioEl!.play().catch(() => undefined)
      } else {
        ttsQueue = []
        ttsQueueIdx = 0
        // 播完仍有重试耗尽的失败段 → 不再装作完整（2026-10-11 F1：跳句不可接受），
        // 报 failed 让 UI 提示重试；重放走合成缓存，只补失败句（自愈）。
        const cur = get().currentMessageId
        const st = cur ? getSynth(cur, useVoicePreferenceStore.getState().voice) : null
        if (st && st.skippedCount > 0) {
          set({ status: 'idle', currentMessageId: null, error: 'failed' })
        } else {
          set({ status: 'idle', currentMessageId: null, error: null })
        }
      }
    })
    return ttsAudioEl
  }

  const startPlayback = (messageId: string, urls: string[]) => {
    ttsQueue = urls
    ttsQueueIdx = 0
    const el = ensureAudio()
    el.src = urls[0]
    void el
      .play()
      .then(() => set({ status: 'playing', currentMessageId: messageId }))
      .catch(() => set({ status: 'idle', currentMessageId: null, error: null }))
  }

  const submitSegment = (messageId: string, voice: string, text: string) => {
    const state = getSynth(messageId, voice)
    const pendingKey = `${messageId}:${voice}:${text}`

    // 检查是否已在待处理队列中
    if (pendingSegments.has(pendingKey)) {
      return Promise.resolve()
    }
    // 第一时间检查 quota：已在队列中的段如果 quota 已耗损，跳过调用
    if (state.quotaExhausted) {
      return Promise.resolve()
    }

    pendingSegments.add(pendingKey)
    const seg: Segment = { text, urls: [], status: 'pending' }
    state.segments.push(seg)
    return runSynth(() => ttsService.synthesize(text, voice))
      .then((res) => {
        pendingSegments.delete(pendingKey)
        if ('error' in res) {
          if (res.error === 'quota') {
            state.quotaExhausted = true // 停止后续增量
            seg.status = 'failed'
            return
          }
          if (res.error === 'empty') {
            // 清洗后全符号段（表格分隔行/围栏残片）：标记 ready + 空 urls，
            // 消费循环直接跳过——不重试、不计失败、不报错
            seg.status = 'ready'
            seg.urls = []
            return
          }
          state.lastError = res.error === 'rate_limited' ? 'rate_limited' : 'failed'
          seg.status = 'failed'
          return
        }
        if (res.urls?.length) {
          seg.urls = res.urls
          seg.status = 'ready'
        } else {
          seg.status = 'failed'
        }
      })
      .catch(() => {
        pendingSegments.delete(pendingKey)
        seg.status = 'failed'
      })
  }

  /** 段级合成 + 退避重试（2026-10-11 F1：跳句不可接受——429/网络瞬断按 1.2s/2.4s/3.6s 退避重试至 3 次；
   *  耗尽才置 failed 并计入 skippedCount（播完向 UI 报错，重放走缓存自愈补齐）） */
  const submitWithRetry = (messageId: string, voice: string, seg: Segment) => {
    const tryOnce = (attempt: number) => {
      seg.attempts = attempt
      void runSynth(() => ttsService.synthesize(seg.text, voice))
        .then((res) => {
          if ('error' in res) {
            if (res.error === 'quota') {
              getSynth(messageId, voice).quotaExhausted = true
              seg.status = 'failed'
            } else if (attempt < 3) {
              setTimeout(() => tryOnce(attempt + 1), 1200 * attempt)
            } else {
              seg.status = 'failed'
              const st = getSynth(messageId, voice)
              st.skippedCount += 1
              st.lastError = res.error === 'rate_limited' ? 'rate_limited' : 'failed'
            }
          } else if (res.urls?.length) {
            seg.urls = res.urls
            seg.status = 'ready'
          } else if (attempt < 3) {
            setTimeout(() => tryOnce(attempt + 1), 1200 * attempt)
          } else {
            seg.status = 'failed'
            const st = getSynth(messageId, voice)
            st.skippedCount += 1
            st.lastError = 'failed'
          }
        })
        .catch(() => {
          if (attempt < 3) {
            setTimeout(() => tryOnce(attempt + 1), 1200 * attempt)
          } else {
            seg.status = 'failed'
            const st = getSynth(messageId, voice)
            st.skippedCount += 1
            st.lastError = 'failed'
          }
        })
    }
    tryOnce(1)
  }

  /** 消费 Segment[]：ready 立即播、pending 边播边等（3s 静默缓冲，超时转 loading）、failed 回退重试 */
  const playSegments = (messageId: string, voice: string, segs: Segment[]) => {
    set({ status: 'playing', currentMessageId: messageId, error: null })
    const runFrom = (startIdx: number) => {
      // 找到下一个需要播放的段 urls
      const urls: string[] = []
      let pendingAt = -1
      for (let i = startIdx; i < segs.length; i++) {
        const seg = segs[i]
        if (seg.status === 'ready') {
          urls.push(...seg.urls)
        } else if (seg.status === 'pending') {
          pendingAt = i
          break
        } else {
          // M2: failed 段重试前检查 quota
          const state = getSynth(messageId, voice)
          if (state.quotaExhausted) {
            set({ status: 'idle', error: 'quota' })
            return
          }
          // 2026-10-11：失败段不再「重试一次即无声跳过」（F1 验收：跳句不可接受）。
          // 交给 submitWithRetry 退避重试至 3 次；耗尽（attempts≥3）才跳段——
          // skippedCount 已在 submitWithRetry 终败时记账，播完由 'ended' 收尾报 error。
          if ((seg.attempts || 0) >= 3) {
            continue
          }
          seg.status = 'pending'
          submitWithRetry(messageId, voice, seg)
          pendingAt = i
          break
        }
      }
      // 终态护栏：扫完全部段无 ready 可播、也无 pending 在途 → 收摊。
      // 否则全段 failed/empty 时状态永远停在 playing（潜伏 bug，流式路径同样存在）。
      // paused 检查防误杀：音频仍在播最后一截时交给 'ended' 事件收尾。
      if (urls.length === 0 && pendingAt < 0) {
        if (!(ttsAudioEl && !ttsAudioEl.paused)) {
          set({ status: 'idle', currentMessageId: null, error: getSynth(messageId, voice).lastError })
        }
        return
      }
      if (urls.length > 0) {
        // I1: 判断是否正在播放本消息，追加队列而非重启
        const playing = ttsAudioEl && !ttsAudioEl.paused && get().currentMessageId === messageId && get().status === 'playing' && ttsQueueIdx < ttsQueue.length
        if (playing) {
          ttsQueue.push(...urls) // 追加到队列尾
        } else {
          startPlayback(messageId, urls) // idle/播完/切消息/暂停 → 重启
        }
      }
      if (pendingAt >= 0) {
        // 前面 ready 段在播时静默等；超 3s 仍 pending → 转合成中提示
        set({ status: urls.length > 0 ? 'playing' : 'loading' })
        const wait = (deadline: number) => {
          setTimeout(() => {
            if (get().currentMessageId !== messageId) {
              return
            }
            if (segs[pendingAt]?.status === 'ready') {
              runFrom(pendingAt)
            } else if (Date.now() < deadline) {
              wait(deadline)
            } else if (segs[pendingAt]?.status === 'pending') {
              set({ status: 'loading' }) // 超时提示「合成中…」
              const next = Date.now() + 30_000
              wait(next)
            } else {
              // 仍 failed：跳过该段，继续后续（一句 invalid 不阻塞整条朗读）
              runFrom(pendingAt + 1)
            }
          }, 200)
        }
        wait(Date.now() + 3_000)
      }
    }
    runFrom(0)
  }

  return {
    status: 'idle',
    currentMessageId: null,
    error: null,
    play: (messageId, text) => {
      const { status, currentMessageId } = get()
      // 同一条消息：切换 暂停 / 继续
      if (currentMessageId === messageId && ttsAudioEl) {
        if (status === 'playing') {
          ttsAudioEl.pause()
          set({ status: 'paused' })
          return
        }
        if (status === 'paused') {
          void ttsAudioEl
            .play()
            .then(() => set({ status: 'playing' }))
            .catch(() => undefined)
          return
        }
      }
      // 新播放：先停掉当前
      if (ttsAudioEl) {
        ttsAudioEl.pause()
      }
      const voice = useVoicePreferenceStore.getState().voice
      const cache = ttsSynthCache.get(`${messageId}:${voice}`)
      if (cache && cache.segments.length > 0) {
        // M1: 全失败时跳过 playSegments，回退整段合成
        const allFailed = cache.segments.every((s) => s.status === 'failed')
        if (!allFailed) {
          playSegments(messageId, voice, cache.segments)
          return
        }
        // 全失败：走下方整段实时合成兜底（与无 segments 同路径）
      }
      // 兜底：静态切句 → 逐段管线（未开 autoPrefetch 或增量未就绪，含历史消息主路径）。
      // 旧整段一次 POST 对 3000 字回复 = 后端串行 25-70 段 DashScope 调用 45-150s 且任一
      // 段失败整体报错；逐段则首段 ~1-2s 起播、后续边播边合、单段失败重试一次后跳过。
      const state = getSynth(messageId, voice)
      state.segments = [] // 覆盖 allFailed 旧段（M1 分支落到此处）
      state.quotaExhausted = false // 显式播放动作：重查服务端余额（充值后免刷新即可重试）
      state.lastError = null
      state.consumedLen = text.length
      set({ status: 'loading', currentMessageId: messageId, error: null })
      const pieces = splitStaticText(text)
      // submitSegment 的 segments.push 先于 runSynth 同步执行 → IIFE 首轮在首个 await 前
      // 已 push seg0，同 tick 的 playSegments 拿到含 pending seg0 的数组，ready 即起播
      void (async () => {
        for (const piece of pieces) {
          if (getSynth(messageId, voice).quotaExhausted) {
            break
          }
          await submitSegment(messageId, voice, piece)
        }
      })()
      playSegments(messageId, voice, state.segments)
    },
    prefetchStream: async (messageId, text) => {
      const voice = useVoicePreferenceStore.getState().voice
      const state = getSynth(messageId, voice)
      if (state.quotaExhausted) {
        return
      }
      const delta = text.slice(state.consumedLen)
      if (!delta) {
        return
      }
      const buffer = state.pendingBuffer + delta
      const { sentences, remaining } = drainStableSentences(buffer)
      state.pendingBuffer = remaining
      state.consumedLen = text.length
      // 串行提交：等待每个段完成后再提交下一个，确保 quota 错误能及时停止后续段
      for (const sentence of sentences) {
        if (state.quotaExhausted) {
          break
        }
        await submitSegment(messageId, voice, sentence)
      }
    },
    prefetchFlush: (messageId) => {
      const voice = useVoicePreferenceStore.getState().voice
      const state = getSynth(messageId, voice)
      if (state.quotaExhausted) {
        return
      }
      const tail = state.pendingBuffer.trim()
      state.pendingBuffer = ''
      if (tail) {
        submitSegment(messageId, voice, tail)
      }
    },
    stop: () => {
      if (ttsAudioEl) {
        ttsAudioEl.pause()
        ttsAudioEl.src = ''
      }
      ttsQueue = []
      ttsQueueIdx = 0
      set({ status: 'idle', currentMessageId: null, error: null })
    },
    playPrepared: (messageId, urls) => {
      if (!urls || urls.length === 0) {
        return
      }
      startPlayback(messageId, urls)
    },
    appendPrepared: (messageId, urls) => {
      if (!urls || urls.length === 0) {
        return
      }
      const { status, currentMessageId } = get()
      if (currentMessageId === messageId && status !== 'idle') {
        ttsQueue.push(...urls) // 正在播/暂停/首块等待：入队，顺序连播
      }
      // 会话已结束（自然播完或用户停止）则丢弃：无法区分二者，自动续播会把
      // 用户停掉的音频复活；并行块几乎同秒到达，此处丢弃不影响完整性。
    },
  }
})
